import { Module } from 'vuex'

import { ModalStateModuleName, NotificationModuleName } from '../modules'

import { ReviewRequestUseCase } from '@/application/reviewRequest/reviewRequestUseCase'
import { RegisterEntryRequest } from '@/domain/entities/reviewRequest/registerEntryRequest'
import { ReviewRequestItem } from '@/domain/entities/reviewRequest/reviewRequestItem'
import { ReviewRequestRepository } from '@/domain/repositories/reviewRequest/reviewRequestRepository'
import { JudgeStatusType } from '@/domain/value_objects/judge/JudgeStatusType'
import { DisplayLanguageType } from '@/domain/value_objects/language/displayLanguageType'

interface ReviewRequestState {
  reviewRequestList: ReviewRequestItem[]
  freeDescription: string
  reviewJudgeId: string
  reviewJudgeStatus: JudgeStatusType
  isLoading: boolean
  error: Error | null
}

export const reviewRequestModule = (repository: ReviewRequestRepository): Module<ReviewRequestState, any> => {
  const useCase = new ReviewRequestUseCase(repository)

  return {
    namespaced: true,
    state: {
      reviewRequestList: [],
      freeDescription: '',
      reviewJudgeId: '',
      reviewJudgeStatus: 'WAITING',
      isLoading: false,
      error: null
    },
    mutations: {
      setReviewRequestList(state, { reviewList }: { reviewList: ReviewRequestItem[] }) {
        state.reviewRequestList = reviewList
      },
      setFreeDescription(state, { message }: { message: string }) {
        state.freeDescription = message
      },
      setReviewJudgeId(state, { judgeId }: { judgeId: string }) {
        state.reviewJudgeId = judgeId
      },
      setReviewJudgeStatus(state, { status }: { status: JudgeStatusType }) {
        state.reviewJudgeStatus = status
      },
      setIsLoading(state, isLoading: boolean) {
        state.isLoading = isLoading
      },
      setError(state, error: Error | null) {
        state.error = error
      }
    },
    actions: {
      async getReviewRequestList(
        { commit },
        { status, displayLanguage }: { status: string; displayLanguage: DisplayLanguageType }
      ) {
        commit('setIsLoading', true)
        commit('setError', null)
        try {
          const res = await useCase.getReviewRequestList(status, displayLanguage)
          if (res == null) return

          res.sort((a, b) => {
            if (a.level < b.level) {
              return -1
            }
            if (a.level > b.level) {
              return 1
            }
            return 0
          })
          commit('setReviewRequestList', { reviewList: res })
        } catch (e) {
          commit('setError', e)
        } finally {
          commit('setIsLoading', false)
        }
      },
      async postRegisterEntry(
        { commit, dispatch },
        {
          judgeId,
          requests,
          message,
          displayLanguage
        }: { judgeId: string; requests: string[]; message: string; displayLanguage: DisplayLanguageType }
      ) {
        commit('setIsLoading', true)
        commit('setError', null)
        try {
          const request: RegisterEntryRequest = {
            judgeId: judgeId,
            requests: requests,
            message: message,
            instruction: null,
            displayLanguage: displayLanguage === 'Japanese' ? 'ja' : 'en',
            apiKey: ''
          }
          await useCase.postRegisterEntry(request)
          await dispatch(`${NotificationModuleName}/registerReviewNotification`, {}, { root: true })
          await commit(`${ModalStateModuleName}/setIsShowReviewAlertModal`, false, { root: true })
        } catch (e) {
          commit('setError', e)
          await dispatch(`${NotificationModuleName}/registerReviewErrorNotification`, {}, { root: true })
        } finally {
          commit('setIsLoading', false)
        }
      }
    },
    getters: {
      reviewRequestList: state => state.reviewRequestList,
      freeDescription: state => state.freeDescription,
      reviewJudgeId: state => state.reviewJudgeId,
      reviewJudgeStatus: state => state.reviewJudgeStatus,
      isLoading: state => state.isLoading,
      error: state => state.error
    }
  }
}
